import { Button, EffectsIcon } from "@au-schein-de/au-ui-library";
import { GENETICS_OPTIONS_MAP } from "@dransay/api/models";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { Carousel } from "~/components/carousel.component";
import { FilterChip } from "./active-filters/filter-chip.component";
import { isNamedOptionsArray } from "./active-filters/format-filter-value";
import type {
	FiltersCategory,
	FiltersConfig,
	FiltersState,
	RemoveFilterFn,
} from "./types";
import { createInitialFilters } from "./use-filters.hook";
import { isFilterActive, mapFiltersByCategories } from "./utils";

interface ActiveFiltersProps {
	filters: FiltersState;
	config: FiltersConfig;
	onRemove: RemoveFilterFn;
	onClearAll: () => void;
	onTopProducerRemoved?: () => void;
}

export const ActiveFilters: FC<ActiveFiltersProps> = ({
	filters,
	config,
	onRemove,
	onClearAll,
	onTopProducerRemoved,
}) => {
	const { t } = useTranslation("product");

	const defaultFilters = createInitialFilters();

	const getFilterRenderStrategy = (
		category: FiltersCategory,
		filters: FiltersState,
		config: FiltersConfig,
		onRemove: RemoveFilterFn,
	) => {
		const values = filters[category];

		switch (category) {
			case "thc":
			case "cbd": {
				if (Array.isArray(values)) {
					return values.map((value) => (
						<FilterChip
							key={`${category}-${value}`}
							category={category}
							value={value.toString()}
							capitalize={false}
							onRemove={onRemove}
							showCategory
						/>
					));
				}

				return [];
			}

			case "priceRanges": {
				if (Array.isArray(values)) {
					return values.map((value) => (
						<FilterChip
							key={`${category}-${value}`}
							category={category}
							value={value.toString()}
							onRemove={onRemove}
						/>
					));
				}

				return [];
			}

			case "flavors":
			case "complaints": {
				if (Array.isArray(values)) {
					return values.map((value) => (
						<FilterChip
							key={`${category}-${value}`}
							category={category}
							value={value.toString()}
							onRemove={onRemove}
						/>
					));
				}

				return [];
			}

			case "effects": {
				if (Array.isArray(values)) {
					return values.map((value) => (
						<FilterChip
							key={`${category}-${value}`}
							category={category}
							value={value.toString()}
							onRemove={onRemove}
							icon={<EffectsIcon name={value.toString()} size="sm" />}
						/>
					));
				}

				return [];
			}

			case "terpenes": {
				if (Array.isArray(values)) {
					return values.map((value) => {
						const terpene = config.terpenes.find((t) => t.name === value);
						return (
							<FilterChip
								key={`${category}-${value}`}
								category={category}
								value={value.toString()}
								onRemove={onRemove}
								color={terpene?.color}
							/>
						);
					});
				}

				return [];
			}

			case "irradiation": {
				if (Array.isArray(values)) {
					return values.map((value) => (
						<FilterChip
							key={`${category}-${value}`}
							category={category}
							value={value.toString()}
							onRemove={onRemove}
						/>
					));
				}

				return [];
			}

			case "rating": {
				if (Array.isArray(values)) {
					return values.map((value) => {
						const displayValue =
							value === 0
								? /* @ts-ignore: dynamic key generation */
									t("filters.noRating")
								: `${value} ★`;
						return (
							<FilterChip
								key={`${category}-${value}`}
								category={category}
								value={displayValue}
								onRemove={(category) => onRemove(category, value.toString())}
							/>
						);
					});
				}

				return [];
			}

			case "topProducersLowPrice": {
				if (!values) return [];

				return (
					<FilterChip
						key={category}
						category={category}
						value={category}
						onRemove={(category) => {
							onRemove("topProducersLowPrice", false);
							onRemove("producers", []);
							onTopProducerRemoved?.();
						}}
						capitalize={false}
					/>
				);
			}

			case "producers": {
				const categoryConfig = config[category];

				if (
					Array.isArray(values) &&
					isNamedOptionsArray(categoryConfig) &&
					!filters.topProducersLowPrice
				) {
					return values.map((value) => (
						<FilterChip
							key={`${category}-${value}`}
							category={category}
							value={
								categoryConfig.find(
									({ id }) => id.toString() === value.toString(),
								)?.name ?? value.toString()
							}
							onRemove={(category) => onRemove(category, value.toString())}
						/>
					));
				}

				return [];
			}

			case "expertReview": {
				if (values) {
					return (
						<FilterChip
							key={category}
							category={category}
							value={t("filters.expertReview")}
							onRemove={() => onRemove("expertReview", undefined)}
							capitalize={false}
						/>
					);
				}

				return [];
			}

			case "genetics": {
				if (Array.isArray(values) && values.length > 0) {
					return values.map((value) => (
						<FilterChip
							key={`${category}-${value}`}
							category={category}
							value={
								GENETICS_OPTIONS_MAP[
									value as keyof typeof GENETICS_OPTIONS_MAP
								] ?? value.toString()
							}
							capitalize={false}
							onRemove={(category) => onRemove(category, value.toString())}
						/>
					));
				}

				return [];
			}

			case "category": {
				if (Array.isArray(values) && values.length > 0) {
					return values.map((value) => {
						const categoryData = config.categories.find(
							({ id }) => id === value,
						);

						return (
							<FilterChip
								key={`${category}-${value}`}
								category={category}
								value={categoryData?.name ?? value.toString()}
								onRemove={(category) => onRemove(category, value.toString())}
							/>
						);
					});
				}

				return [];
			}
		}
	};

	const renderActiveFilters = () => {
		return mapFiltersByCategories(filters).flatMap(([category, values]) => {
			const defaultValue = defaultFilters[category];

			if (!isFilterActive(values, defaultValue)) {
				return [];
			}

			return getFilterRenderStrategy(category, filters, config, onRemove);
		});
	};

	const activeFilters = renderActiveFilters();

	if (activeFilters.length === 0) return null;

	return (
		<div className="flex items-center min-w-0 mb-4 px-4 -mx-4">
			<Carousel>{activeFilters}</Carousel>

			<div className="flex-shrink-0">
				<Button
					variant="link"
					onClick={onClearAll}
					className="px-2 md:px-4 py-1 md:py-2 min-h-0 md:min-h-14"
					data-testid="clear-all-filters"
				>
					{t("filters.clearAll")}
				</Button>
			</div>
		</div>
	);
};
